/**
 * Serialize workspace state into compact LLM context.
 *
 * Produces a JSON object that tells the LLM what's currently loaded,
 * which preconditions are met, and what numerical data is available
 * for each compound.
 *
 * **Privacy boundary:** compound IDENTITIES (names, SMILES, structural
 * data) are stripped. Only anonymous indices and NUMERICAL PROPERTIES
 * are included. The LLM can reason about scores, energies, and
 * molecular properties without ever seeing proprietary structures.
 * See feedback_medchem_nomenclature.md in user memory for context on
 * why this boundary exists.
 *
 * @module gibbs/text-to-action/serializeWorkspace
 */

import {
    getAllPreconditionIds,
    evaluatePrecondition,
    fragmentAvailabilityEvidence,
} from '../registry/index';
import { ADMET_RANGE_METADATA } from '../autonomousCampaign/evaluators/admetRanges';
import { hasCurrentDockedPose } from '../compoundLineage';

const ADMET_KEYS = new Set(Object.keys(ADMET_RANGE_METADATA));

/**
 * Safely read a numeric value, returning null if unavailable.
 * @param {function} fn
 * @returns {number|null}
 */
function safeNum(fn) {
    try {
        const v = fn();
        return typeof v === 'number' && Number.isFinite(v)
            ? Math.round(v * 100) / 100
            : null;
    } catch (_) {
        return null;
    }
}

function roundNum(v) {
    return typeof v === 'number' && Number.isFinite(v)
        ? Math.round(v * 100) / 100
        : null;
}

const CORE_ENERGY_TYPES = [
    'vdW',
    'hbonds',
    'ddGs',
    'stress',
    'electrostatics',
];
const INTERACTION_SCORE_TYPES = ['vdW', 'hbonds'];

/**
 * EnergyInfo initializes its aggregate getters to zero before any energy has
 * been requested. Treat those aggregates as evidence only when their source
 * components are actually available; a legitimate computed zero still counts.
 */
function energyTypeAvailable(info, type) {
    if (!info) return false;
    if (typeof info.energyAvailable === 'function') {
        try {
            return info.energyAvailable(type) === true;
        } catch (_) {
            return false;
        }
    }
    if (typeof info.getEnergyEntryByType === 'function') {
        try {
            const entry = info.getEnergyEntryByType(type);
            return !!entry
                && (entry.status == null || entry.status === 'success')
                && typeof entry.energy === 'number'
                && Number.isFinite(entry.energy);
        } catch (_) {
            return false;
        }
    }
    return safeNum(() => info.getEnergyValueByType?.(type)) !== null;
}

function interactionScoreAvailable(info) {
    if (!info) return false;
    if (typeof info.energyScoreAvailable === 'function') {
        try {
            return info.energyScoreAvailable() === true;
        } catch (_) {
            return false;
        }
    }
    return INTERACTION_SCORE_TYPES.every((type) => energyTypeAvailable(info, type));
}

function totalEnergyAvailable(info) {
    if (!info) return false;
    if (typeof info.notRequested === 'function') {
        try {
            if (info.notRequested() === true) return false;
        } catch (_) {
            return false;
        }
    }
    return CORE_ENERGY_TYPES.some((type) => energyTypeAvailable(info, type));
}

function availableEnergyValue(info, type) {
    return energyTypeAvailable(info, type)
        ? safeNum(() => info.getEnergyValueByType(type))
        : null;
}

/**
 * Extract the energy report from a compound's energyInfo,
 * returning only numeric values that are actually computed.
 *
 * @param {Object} compound
 * @returns {Object|null}
 */
function serializeEnergies(compound) {
    const info = compound.energyInfo || compound.getEnergyInfo?.();
    const gninaScope = serializeGninaScope(compound);
    if (!info && !gninaScope) return null;

    const report = gninaScope ? { ...gninaScope } : {};
    let hasAny = !!gninaScope;

    // Core energy components. `interactionScore` is the live BMaps display score.
    // `interactionEnergyKcalMol` is the autonomous-campaign contract: vdW +
    // electrostatics when both components are available.
    const score = interactionScoreAvailable(info)
        ? safeNum(() => info.getEnergyScore())
        : null;
    if (score != null) {
        report.interactionScore = score;
        hasAny = true;
    }

    const total = totalEnergyAvailable(info)
        ? safeNum(() => info.getEnergyTotal())
        : null;
    if (total != null) {
        report.totalEnergy = total;
        report.totalKcalMol = total;
        hasAny = true;
    }

    const vdw = availableEnergyValue(info, 'vdW');
    if (vdw != null) { report.vdW = vdw; hasAny = true; }

    const elec = availableEnergyValue(info, 'electrostatics');
    if (elec != null) {
        report.electrostatics = elec;
        report.coulomb = elec;
        hasAny = true;
    }

    if (vdw != null && elec != null) {
        report.interactionEnergyKcalMol = roundNum(vdw + elec);
    } else if (score != null) {
        // Older/live BMaps builds may only expose the aggregate score. Keep the
        // canonical field populated as a fallback, but never let it contradict
        // available vdW/electrostatic components.
        report.interactionEnergyKcalMol = score;
    }

    const hb = availableEnergyValue(info, 'hbonds');
    if (hb != null) { report.hbonds = hb; hasAny = true; }

    const solv = availableEnergyValue(info, 'ddGs');
    if (solv != null) { report.solvation = solv; hasAny = true; }

    const stress = availableEnergyValue(info, 'stress');
    if (stress != null) { report.stress = stress; hasAny = true; }

    // GNINA / docking scores (ExtraTypes)
    const gnina = availableEnergyValue(info, 'gninaAffinity');
    if (gnina != null) { report.gninaAffinity = gnina; hasAny = true; }

    const gninaPose = availableEnergyValue(info, 'gninaPoseScore');
    if (gninaPose != null) { report.gninaPoseScore = gninaPose; hasAny = true; }

    const docking = availableEnergyValue(info, 'dockingScore');
    if (docking != null) { report.dockingScore = docking; hasAny = true; }

    return hasAny ? report : null;
}

function serializeGninaScope(compound) {
    const affinity = coerceFiniteNumber(compound.getProperty?.('gnina_scoring', 'affinity'));
    const poseScore = coerceFiniteNumber(compound.getProperty?.('gnina_scoring', 'cnn_score'));
    if (affinity === null && poseScore === null) return null;

    const report = {};
    if (affinity !== null) report.gninaAffinity = affinity;
    if (poseScore !== null) report.gninaPoseScore = poseScore;
    return report;
}

function serializePose(energies) {
    if (!energies) return null;
    const score = coerceFiniteNumber(
        energies.gninaAffinity
        ?? energies.dockingScore
        ?? energies.gninaPoseScore,
    );
    return score === null ? null : {
        score,
        centroidDistanceA: null,
        inPocket: null,
    };
}

/**
 * Extract molecular properties (MW, logP, PSA, etc.) from a compound.
 *
 * @param {Object} compound
 * @returns {Object|null}
 */
function serializeMolProps(compound) {
    const props = compound.getMolProps?.();
    if (!props || typeof props !== 'object') return null;

    const report = {};
    let hasAny = false;

    // Map internal prop names to clean LLM-readable keys.
    // Some keys contain spaces/special chars so they must be quoted.
    /* eslint-disable quote-props */
    const keyMap = {
        'Mol. Weight': 'MW',
        'LogP': 'logP',
        'PSA': 'PSA',
        'HB Acceptors': 'HBA',
        'HB Donors': 'HBD',
        '# Heavy Atoms': 'heavyAtoms',
        'Charge': 'charge',
        '# Rot. Bonds': 'rotatableBonds',
        'Fraction Sp3': 'fsp3',
        '# Lipinski Violations': 'lipinskiViolations',
    };
    /* eslint-enable quote-props */

    for (const [internalKey, cleanKey] of Object.entries(keyMap)) {
        const val = props[internalKey];
        if (val != null && typeof val === 'number' && Number.isFinite(val)) {
            report[cleanKey] = Math.round(val * 100) / 100;
            hasAny = true;
        }
    }

    return hasAny ? report : null;
}

function coerceFiniteNumber(raw) {
    if (raw == null) return null;
    const v = typeof raw === 'string' ? Number(raw) : raw;
    return typeof v === 'number' && Number.isFinite(v)
        ? Math.round(v * 100) / 100
        : null;
}

function coerceFiniteNumberWithoutRounding(raw) {
    if (raw == null) return null;
    const value = typeof raw === 'string' ? Number(raw) : raw;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Extract MapLight ADMET values from scoped compound properties.
 *
 * MapLight writes values under the `maplight` property scope as `ADMET_*`
 * entries. These are numeric prediction outputs, so they are safe to expose
 * across the masking boundary; target, compound name, SMILES, and structure
 * identity remain omitted.
 *
 * @param {Object} compound
 * @returns {Object|null}
 */
function serializeAdmet(compound) {
    const group = compound.getPropertyGroup?.('maplight');
    const keys = [];
    if (group && typeof group.listProperties === 'function') {
        for (const prop of group.listProperties()) {
            if (prop && typeof prop.name === 'string' && ADMET_KEYS.has(prop.name)) {
                keys.push(prop.name);
            }
        }
    }

    const report = {};
    let hasAny = false;
    for (const key of keys) {
        // Preserve the prediction's full finite precision across the masked
        // evidence boundary. Strict configured ranges (for example `<0.5`)
        // must be evaluated before presentation rounding.
        const val = coerceFiniteNumberWithoutRounding(
            compound.getProperty?.('maplight', key),
        );
        if (val !== null) {
            report[key] = val;
            hasAny = true;
        }
    }

    return hasAny ? report : null;
}

const NATIVE_BINDER_LABEL = 'Co-Crystal Ligand';
const WORKFLOW_ROLE_SCOPE = 'workflow_roles';
const NATIVE_REFERENCE_ROLE = 'nativeReference';
const DERIVATION_LABELS = [
    'Docked',
    'Duplicated',
    'Terminal Replace',
    'Fragment Grow',
    'Search Nearby',
    'Generated',
    'Sample compound',
    'Grown',
    'Minimized',
    'Modified',
    'Energy Minimized',
    'Renamed',
];

function heritageLabels(compound) {
    const heritage = compound?.getHeritage?.();
    if (!Array.isArray(heritage)) return [];
    return heritage
        .map((unit) => (unit && typeof unit.label === 'string' ? unit.label : null))
        .filter(Boolean);
}

function carriesNativeReferenceRole(compound) {
    try {
        return compound?.getProperty?.(WORKFLOW_ROLE_SCOPE, NATIVE_REFERENCE_ROLE) === true;
    } catch (_) {
        return false;
    }
}

function serializeCompoundLineage(compound) {
    const labels = heritageLabels(compound);
    const hasNativeLabel = labels.includes(NATIVE_BINDER_LABEL);
    const hasDerivation = labels.some((label) => DERIVATION_LABELS.includes(label));
    const out = {};
    if (carriesNativeReferenceRole(compound) && hasNativeLabel && !hasDerivation) {
        out.isNativeBinder = true;
    }
    // Only current explicit Docked heritage proves that docking actually ran.
    // Later structure-changing operations invalidate it; pose-preserving
    // rename, duplicate, and minimization operations retain that provenance.
    if (hasCurrentDockedPose(labels)) out.isDockedPose = true;
    return out;
}

/**
 * How this workspace's own crystal ligands are roled, by real display name:
 * which carry `workflow_roles.nativeReference` and which do not.
 *
 * BROWSER-UI ONLY, and deliberately **not** called from `serializeWorkspace()`:
 * it returns unmasked display names, so nothing it produces may cross into the
 * engine, model, evaluator, or log. Its only consumer is the pre-submission
 * disclosure in the campaign panel.
 *
 * Nothing in a workspace distinguishes "unmarked reference" from "candidate that
 * happens to be a crystal ligand". Both are legitimate and neither is a defect:
 * workspaces A and B are deliberately the latter, and a run that docks their
 * crystal ligand is the reviewed, signed behavior. So an unmarked ligand is
 * reported as a plain fact about what the run will do — never as a warning, and
 * never with advice to change it. It exists so someone loading an unfamiliar
 * workspace can recognise a molecule they did not mean to dock.
 *
 * @param {Object} workspace - live BMaps workspace (App.Workspace)
 * @returns {{marked: string[], unmarked: string[]}} display names by role state
 */
export function crystalLigandRoleSummary(workspace) {
    const compounds = workspace?.getLoadedCompounds?.() || [];
    const marked = [];
    const unmarked = [];
    compounds.forEach((compound) => {
        const labels = heritageLabels(compound);
        if (!labels.includes(NATIVE_BINDER_LABEL)) return;
        // A pose docked or grown from the crystal ligand is a derived candidate,
        // not the reference itself.
        if (labels.some((label) => DERIVATION_LABELS.includes(label))) return;
        const name = compound?.displayName;
        if (typeof name !== 'string' || name.length === 0) return;
        (carriesNativeReferenceRole(compound) ? marked : unmarked).push(name);
    });
    return { marked, unmarked };
}

/**
 * Serialize workspace state for LLM prompt context.
 *
 * @param {Object} workspace - BMaps workspace state (App.Workspace or stub)
 * @param {Object} [options]
 * @param {function(Object): ?string} [options.proteinRole] resolves a MapCase to
 *   `'target'`, `'off-target'`, or null when no explicit role has been set.
 *   INJECTED because the role lives in the Redux project target list, keyed by
 *   MapCase object, and this module (like the rest of gibbs) is store-free.
 *   Production wiring passes a reader built from `getTargetInfoMap()`; unit tests
 *   and any unwired caller leave it out and get no roles, which downstream
 *   contracts treat as "unknown" and fail closed on.
 * @returns {Object} Compact workspace summary with numerical data
 */
export function serializeWorkspace(workspace, options = {}) {
    const state = {
        proteins: [],
        compounds: [],
        activeCompound: null,
        compoundCount: 0,
        fragmentMapLoaded: false,
        fragmentAvailability: {
            status: 'unknown',
            source: 'workspace-fragment-info',
            entryCount: null,
        },
        preconditionsMet: [],
    };

    state.fragInfoCount = 0;
    const caseDatas = workspace.allCaseData?.() || [];
    // Every data connection owns an internal no-protein CaseData placeholder.
    // It is not a target and must not vote against the capability of loaded
    // target cases. Multiple real targets still have to agree below.
    const targetCases = caseDatas.filter((caseData) => caseData?.mapCase);
    const fragmentEvidence = targetCases.map(fragmentAvailabilityEvidence);
    const allAvailable = fragmentEvidence.length > 0
        && fragmentEvidence.every((evidence) => evidence.status === 'available');
    const allUnavailable = fragmentEvidence.length > 0
        && fragmentEvidence.every((evidence) => evidence.status === 'unavailable');
    const oneSource = fragmentEvidence.length > 0
        && fragmentEvidence.every((evidence) => evidence.source === fragmentEvidence[0].source)
        ? fragmentEvidence[0].source
        : 'workspace-fragment-info';
    if (allAvailable) {
        const fragmentCount = fragmentEvidence.reduce(
            (count, evidence) => count + evidence.entryCount,
            0,
        );
        state.fragmentMapLoaded = true;
        state.fragInfoCount = fragmentCount;
        state.fragmentAvailability = {
            status: 'available',
            source: oneSource,
            entryCount: fragmentCount,
        };
    } else if (allUnavailable) {
        state.fragmentAvailability = {
            status: 'unavailable',
            source: oneSource,
            entryCount: 0,
        };
    } else if (fragmentEvidence.length > 0) {
        state.fragmentAvailability = {
            status: 'unknown',
            source: oneSource,
            entryCount: null,
        };
    }

    // A role only distinguishes something when more than one structure is
    // loaded, and emitting it unconditionally would rewrite the signed
    // workspace baseline (and the workspaceBindingId fingerprint, which hashes
    // it) of every existing single-protein campaign. So: one protein serializes
    // exactly as it always has, and the role appears only where it decides
    // something.
    const roleReported = targetCases.length > 1;
    const readRole = typeof options.proteinRole === 'function'
        ? options.proteinRole
        : null;

    // Serialize loaded proteins
    for (const cd of caseDatas) {
        const mapCase = cd.mapCase;
        if (mapCase) {
            const role = roleReported && readRole ? readRole(mapCase) : null;
            state.proteins.push({
                pdbId: mapCase.pdbId || mapCase.name || 'unknown',
                name: mapCase.displayName || mapCase.name || '',
                // Falling back to `mapCase.isTarget` keeps this field exactly as
                // it has always behaved wherever no role is reported. It reads
                // false on every real MapCase — the property does not exist on
                // one — which is precisely why the role has to be injected.
                isTarget: role ? role === 'target' : mapCase.isTarget === true,
                ...(roleReported ? { role: role || 'unknown' } : {}),
            });
        }
    }

    // Which protein each compound sits under. The linkage is not new data — it
    // is the nesting getLoadedCompounds() flattens (Workspace.js:1048) — so this
    // re-derives it from the same iteration rather than inventing an
    // association. Reported only alongside roles, for the same reason.
    const proteinIndexByCompound = new Map();
    if (roleReported) {
        let proteinIndex = 0;
        for (const cd of caseDatas) {
            if (!cd.mapCase) continue;
            proteinIndex += 1;
            for (const compound of cd.getCompounds?.() || []) {
                proteinIndexByCompound.set(compound, proteinIndex);
            }
        }
    }

    // Serialize loaded compounds — ANONYMIZED. No names, no SMILES,
    // no structural identity. Only anonymous index + numerical data.
    const compounds = workspace.getLoadedCompounds?.() || [];
    state.compoundCount = compounds.length;

    for (let i = 0; i < compounds.length; i++) {
        const c = compounds[i];
        const proteinIndex = proteinIndexByCompound.get(c);
        const entry = {
            id: i + 1, // anonymous 1-based index
            has3D: (c.getAtoms?.()?.length || 0) > 0,
            atomCount: c.getAtoms?.()?.length || 0,
            ...(proteinIndex ? { proteinIndex } : {}),
            ...serializeCompoundLineage(c),
        };

        // Energies (only if computed)
        const energies = serializeEnergies(c);
        if (energies) {
            const pose = serializePose(energies);
            if (pose) entry.pose = pose;
            entry.energies = energies;
        }

        // Molecular properties (only if available)
        const molProps = serializeMolProps(c);
        if (molProps) entry.properties = molProps;

        const admet = serializeAdmet(c);
        if (admet) entry.admet = admet;

        state.compounds.push(entry);
    }

    // Active compound
    const active = workspace.getActiveCompound?.();
    if (active) {
        const idx = compounds.indexOf(active);
        state.activeCompound = idx >= 0 ? idx + 1 : null;
    }

    // Evaluate all preconditions
    const allChecks = getAllPreconditionIds();
    for (const checkId of allChecks) {
        try {
            if (evaluatePrecondition(checkId, workspace)) {
                state.preconditionsMet.push(checkId);
            }
        } catch (_) {
            // Skip failed evaluations
        }
    }

    return state;
}

/**
 * Get anonymized compound data for the filter/rank shortcut.
 * Returns a richer data set than serializeWorkspace (which is
 * optimized for prompt token budget). This function is called
 * directly by the filter/rank shortcut, not by the LLM prompt.
 *
 * @param {Object} workspace
 * @returns {Array<Object>} Array of anonymized compound data
 */
export function getCompoundData(workspace) {
    const compounds = workspace.getLoadedCompounds?.() || [];
    const active = workspace.getActiveCompound?.();

    return compounds.map((c, i) => {
        const entry = {
            id: i + 1,
            isActive: c === active,
            atomCount: c.getAtoms?.()?.length || 0,
        };

        const energies = serializeEnergies(c);
        if (energies) entry.energies = energies;

        const molProps = serializeMolProps(c);
        if (molProps) entry.properties = molProps;

        return entry;
    });
}
